import type { FocusEvent, KeyboardEvent } from 'react';
import { useId, useState } from 'react';

export type RowKeyboardProps = {
  tabIndex: 0 | -1;
  'aria-describedby': string;
  onFocus: (event: FocusEvent<HTMLElement>) => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
};

/**
 * Keyboard access to the rows that open or pick something (roving tabindex): together they are one
 * Tab stop, held by the last focused row, else the first. ArrowUp/ArrowDown move to the previous/next
 * such row, Home/End to the first/last, and Enter or Space does what a click does.
 *
 * `getRowKeyboardProps` returns the props for a row by id, or `undefined` for a row outside `rowIds`,
 * which stays exactly as it is. Every interactive row is described by the element with `hintId`.
 */
export function useRowKeyboardNavigation(rowIds: string[]) {
  const [focusedRowId, setFocusedRowId] = useState<string>();
  const hintId = useId();
  const interactiveRowIds = new Set(rowIds);
  // Derived rather than synced: once the focused row is gone (another page, a search), the first row
  // holds the Tab stop again, so it never points at a row that is not there.
  const tabStopRowId = focusedRowId !== undefined && interactiveRowIds.has(focusedRowId) ? focusedRowId : rowIds[0];

  const getRowKeyboardProps = (
    rowId: string,
    activate: (event: KeyboardEvent) => void
  ): RowKeyboardProps | undefined => {
    if (!interactiveRowIds.has(rowId)) {
      return undefined;
    }
    return {
      tabIndex: rowId === tabStopRowId ? 0 : -1,
      'aria-describedby': hintId,
      onFocus: (event) => {
        if (event.target === event.currentTarget) {
          setFocusedRowId(rowId);
        }
      },
      onKeyDown: (event) => {
        // A key on a checkbox, the row menu or anything else focusable inside the row is theirs.
        if (event.target !== event.currentTarget) {
          return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
          // Space would scroll the page, Enter could submit a surrounding form.
          event.preventDefault();
          if (!event.repeat) {
            activate(event);
          }
          return;
        }
        if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
          return;
        }
        const rows = interactiveRowsBeside(event.currentTarget);
        const nextIndex = nextRowIndex(event.key, rows.indexOf(event.currentTarget), rows.length - 1);
        if (nextIndex === undefined) {
          return;
        }
        event.preventDefault();
        rows[nextIndex]?.focus();
      }
    };
  };

  return { getRowKeyboardProps, hintId };
}

// The rows carrying a tabindex are exactly the interactive ones; load-more and empty-state rows carry none.
const interactiveRowsBeside = (row: HTMLElement): HTMLElement[] =>
  Array.from(row.parentElement?.children ?? []).filter(
    (element): element is HTMLElement => element instanceof HTMLElement && element.hasAttribute('tabindex')
  );

const nextRowIndex = (key: string, index: number, lastIndex: number): number | undefined => {
  switch (key) {
    case 'ArrowDown':
      return Math.min(index + 1, lastIndex);
    case 'ArrowUp':
      return Math.max(index - 1, 0);
    case 'Home':
      return 0;
    case 'End':
      return lastIndex;
    default:
      return undefined;
  }
};
